import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { ORBIT_PANEL_WIDTH } from 'containers/Orbit/constants';

/**
 * The Orbit launcher is pinned to the same bottom-right corner this bar lays its primary button
 * out in, and has to lift itself clear of whichever page happens to render one. The bar is the only
 * thing the two have in common, so it carries the handle `useBottomBarOffset` queries for.
 */
export const BOTTOM_BAR_TEST_ID = 'bottom-bar';

/**
 * Set on the document body by `OrbitShell` while the Orbit panel is open.
 *
 * The panel is a flex sibling of the routed content, so opening it SHRINKS the page - but this bar
 * is fixed at full viewport width and is not part of that row, so it keeps laying its buttons out
 * flush to the viewport edge and both of them end up underneath the panel. Measured on a campaign,
 * a rule and a reward step: `elementFromPoint` at the centre of Cancel and of Next returned the
 * Orbit composer, i.e. neither button could be clicked at all (browser-recheck16-20260916.md).
 */
export const ORBIT_PANEL_OPEN_CLASS = 'orbit-panel-open';

const Wrapper = styled.div`
  position: fixed;
  bottom: 0;
  background: #ffffff;
  border: solid 1px #e9e9e9;
  width: 100%;
  left: 0;
  z-index: 10;
  padding: 1rem;
  transition: padding-right 0.24s cubic-bezier(0.32, 0.72, 0, 1);

  body.${ORBIT_PANEL_OPEN_CLASS} & {
    padding-right: calc(1rem + ${ORBIT_PANEL_WIDTH});
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Content = styled.div`
  display: flex;
  justify-content: flex-end;
`;

interface BottomBarProps {
  children: ReactNode;
}

export const BottomBar = ({ children }: BottomBarProps) => (
  <Wrapper data-testid={BOTTOM_BAR_TEST_ID}>
    <Content>{children}</Content>
  </Wrapper>
);

export default BottomBar;
